import React from 'react';
import { useTranslation } from 'react-i18next';

export interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: React.ReactNode;
  onClose?: () => void;
  className?: string;
  action?: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  onClose,
  className = '',
  action,
}) => {
  const { t } = useTranslation();
  
  // Background colors based on alert type
  const bgColors = {
    success: 'bg-green-100 dark:bg-green-900',
    error: 'bg-red-100 dark:bg-red-900',
    warning: 'bg-yellow-100 dark:bg-yellow-900',
    info: 'bg-blue-100 dark:bg-blue-900',
  };

  // Text colors based on alert type
  const textColors = {
    success: 'text-green-800 dark:text-green-100',
    error: 'text-red-800 dark:text-red-100',
    warning: 'text-yellow-800 dark:text-yellow-100',
    info: 'text-blue-800 dark:text-blue-100',
  };

  // Border colors based on alert type
  const borderColors = {
    success: 'border-green-500 dark:border-green-600',
    error: 'border-red-500 dark:border-red-600',
    warning: 'border-yellow-500 dark:border-yellow-600',
    info: 'border-blue-500 dark:border-blue-600',
  };

  return (
    <div
      className={`p-4 border-l-4 ${bgColors[type]} ${textColors[type]} ${borderColors[type]} rounded-r ${className}`}
      role="alert"
    >
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          {title && <h3 className="font-bold mb-1">{title}</h3>}
          <p>{message || ''}</p>
          {action && <div className="mt-3">{action}</div>}
        </div>
        
        {onClose && (
          <button
            type="button"
            className={`ml-4 ${textColors[type]} hover:opacity-75`}
            onClick={onClose}
            aria-label={t('common.actions.close') as string}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;